import React from "react";
import { useParams } from "react-router-dom";
import { customersApi } from "../../api/customers";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
import SettingsSuggestRoundedIcon from "@mui/icons-material/SettingsSuggestRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

const ORANGE = "#F35A3A";
const INK = "var(--wp-panel-text-primary, #19242F)";
const MUTED = "rgba(var(--wp-panel-ink-rgb, 25,36,47),.66)";

const ACTIONS = [
  ["whatsapp", "WhatsApp", <WhatsAppIcon fontSize="small" />],
  ["call", "Llamar", <CallRoundedIcon fontSize="small" />],
  ["email", "Email", <EmailRoundedIcon fontSize="small" />],
  ["meeting", "Reunión", <EventRoundedIcon fontSize="small" />],
  ["task", "Tarea", <CheckCircleRoundedIcon fontSize="small" />],
];

function localInputValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 16);
}

function dateLabel(value) {
  if (!value) return "Sin fecha";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin fecha";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "short", timeStyle: "short" }).format(date);
}

export function crmStage(config, key) {
  const normalized = String(key || "new").toLowerCase();
  return config?.stages?.find((stage) => stage.key === normalized) || config?.stages?.find((stage) => stage.key === "new") || { key: normalized, name: normalized || "Nuevo", color: ORANGE, kind: "open" };
}

export function CrmStageChip({ config, stageKey, size = "small", clickable = false, onClick }) {
  const stage = crmStage(config, stageKey);
  return (
    <Chip
      size={size}
      clickable={clickable}
      onClick={onClick}
      label={stage.name}
      sx={{
        fontWeight: 950,
        bgcolor: `${stage.color}18`,
        color: "var(--wp-panel-text-primary, #19242F)",
        border: `1px solid ${stage.color}55`,
        "&::before": { content: '""', width: 7, height: 7, borderRadius: "50%", bgcolor: stage.color, ml: .8, mr: -.15 },
      }}
    />
  );
}

export function CrmNextActionLabel({ crm }) {
  const next = crm?.nextAction || {};
  if (!next.type || !next.dueAt || next.status !== "pending") return <Typography variant="caption" sx={{ color: MUTED, fontWeight: 750 }}>Sin seguimiento programado</Typography>;
  const due = new Date(next.dueAt);
  const now = new Date();
  const overdue = due.getTime() < now.getTime();
  const action = ACTIONS.find(([key]) => key === next.type)?.[1] || "Seguimiento";
  return (
    <Stack spacing={.15}>
      <Typography variant="body2" sx={{ fontWeight: 900, color: overdue ? "error.main" : INK }}>{action} · {dateLabel(next.dueAt)}</Typography>
      {next.note ? <Typography variant="caption" noWrap sx={{ color: MUTED, maxWidth: 260 }}>{next.note}</Typography> : null}
    </Stack>
  );
}

function activityIcon(activity) {
  if (activity?.type === "whatsapp") return <WhatsAppIcon sx={{ fontSize: 17 }} />;
  if (activity?.type === "call") return <CallRoundedIcon sx={{ fontSize: 17 }} />;
  if (activity?.type === "email") return <EmailRoundedIcon sx={{ fontSize: 17 }} />;
  if (activity?.type === "meeting") return <EventRoundedIcon sx={{ fontSize: 17 }} />;
  if (activity?.type === "stage_change") return <SettingsSuggestRoundedIcon sx={{ fontSize: 17 }} />;
  return <NotesRoundedIcon sx={{ fontSize: 17 }} />;
}

export function CustomerCrmTrackingDialog({ open, customer, config, busy = false, onClose, onSave, onActivity }) {
  const [form, setForm] = React.useState(null);
  const [activity, setActivity] = React.useState({ type: "whatsapp", note: "", completeNextAction: true });
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open || !customer) return;
    setError("");
    setForm({
      stageKey: customer.crm?.stageKey || "new",
      source: customer.crm?.source || customer.origin || "manual",
      temperature: customer.crm?.temperature || "warm",
      estimatedValue: customer.crm?.estimatedValue ?? "",
      currency: customer.crm?.currency || "ARS",
      closeReason: customer.crm?.closeReason || "",
      nextAction: {
        type: customer.crm?.nextAction?.type || "whatsapp",
        dueAt: localInputValue(customer.crm?.nextAction?.dueAt),
        note: customer.crm?.nextAction?.note || "",
        status: "pending",
      },
    });
    setActivity({ type: "whatsapp", note: "", completeNextAction: customer.crm?.nextAction?.status === "pending" });
  }, [customer, open]);

  if (!form) return null;
  const selectedStage = crmStage(config, form.stageKey);
  const activeStages = (config?.stages || []).filter((stage) => stage.active !== false);
  const activities = customer?.crm?.activities || [];

  const save = async () => {
    setError("");
    try {
      await onSave?.({
        ...form,
        estimatedValue: form.estimatedValue === "" ? null : Number(form.estimatedValue),
        nextAction: selectedStage.kind === "open"
          ? { ...form.nextAction, dueAt: form.nextAction.dueAt || null }
          : { type: "", dueAt: null, note: "", status: "done" },
      });
    } catch (err) {
      setError(err?.message || "No se pudo guardar el seguimiento.");
    }
  };

  const addActivity = async () => {
    if (!activity.note.trim()) { setError("Escribí qué pasó antes de registrar la actividad."); return; }
    setError("");
    try {
      const channel = activity.type === "whatsapp" ? "whatsapp" : activity.type === "call" ? "phone" : activity.type === "email" ? "email" : "none";
      await onActivity?.({ ...activity, channel, direction: ["whatsapp", "call", "email"].includes(activity.type) ? "outbound" : "none" });
      setActivity((current) => ({ ...current, note: "" }));
    } catch (err) {
      setError(err?.message || "No se pudo registrar la actividad.");
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{ py: 1.4, pr: 6 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontWeight: 1000, fontSize: 20, color: INK }}>{customer?.name || "Seguimiento"}</Typography>
          <CrmStageChip config={config} stageKey={form.stageKey} />
          <Chip size="small" icon={<LocalFireDepartmentRoundedIcon />} label={form.temperature === "hot" ? "Caliente" : form.temperature === "cold" ? "Frío" : "Tibio"} variant="outlined" sx={{ fontWeight: 850 }} />
        </Stack>
        <IconButton size="small" onClick={onClose} disabled={busy} sx={{ position: "absolute", right: 12, top: 12 }}><CloseRoundedIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 1.6, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.018)" }}>
        {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
        <Grid container spacing={1.2}>
          <Grid item xs={12} lg={7.5}>
            <Stack spacing={1.15}>
              <Paper variant="outlined" sx={{ p: 1.25, borderColor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.08)" }}>
                <Typography variant="caption" sx={{ fontWeight: 950, color: MUTED, textTransform: "uppercase", letterSpacing: ".06em" }}>Etapa comercial</Typography>
                <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap sx={{ mt: .7 }}>
                  {activeStages.map((stage) => (
                    <Button
                      key={stage.key}
                      size="small"
                      variant={form.stageKey === stage.key ? "contained" : "outlined"}
                      onClick={() => setForm((current) => ({ ...current, stageKey: stage.key }))}
                      sx={{
                        fontWeight: 950,
                        textTransform: "none",
                        ...(form.stageKey === stage.key ? { bgcolor: stage.color, "&:hover": { bgcolor: stage.color, filter: "brightness(.94)" } } : { borderColor: `${stage.color}70`, color: INK }),
                      }}
                    >
                      {stage.name}
                    </Button>
                  ))}
                </Stack>
                {selectedStage.description ? <Typography variant="caption" sx={{ display: "block", mt: .65, color: MUTED }}>{selectedStage.description}</Typography> : null}
              </Paper>

              <Grid container spacing={1}>
                <Grid item xs={12} sm={4}>
                  <TextField select fullWidth size="small" label="Temperatura" value={form.temperature} onChange={(e) => setForm((c) => ({ ...c, temperature: e.target.value }))}>
                    <MenuItem value="cold">Frío</MenuItem><MenuItem value="warm">Tibio</MenuItem><MenuItem value="hot">Caliente</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Origen" value={form.source} onChange={(e) => setForm((c) => ({ ...c, source: e.target.value }))} placeholder="WhatsApp, web, referido…" /></Grid>
                <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Valor estimado" type="number" value={form.estimatedValue} onChange={(e) => setForm((c) => ({ ...c, estimatedValue: e.target.value }))} InputProps={{ startAdornment: <Typography sx={{ mr: .6, color: MUTED, fontWeight: 800 }}>{form.currency}</Typography> }} /></Grid>
              </Grid>

              {selectedStage.kind === "open" ? (
                <Paper variant="outlined" sx={{ p: 1.25, borderColor: "rgba(243,90,58,.22)", bgcolor: "rgba(243,90,58,.025)" }}>
                  <Typography sx={{ fontWeight: 950, color: INK }}>Próxima acción</Typography>
                  <Typography variant="caption" sx={{ color: MUTED }}>El CRM debería poder responder siempre: “¿qué hacemos con este prospecto y cuándo?”.</Typography>
                  <Grid container spacing={1} sx={{ mt: .2 }}>
                    <Grid item xs={12} sm={4}><TextField select fullWidth size="small" label="Acción" value={form.nextAction.type} onChange={(e) => setForm((c) => ({ ...c, nextAction: { ...c.nextAction, type: e.target.value } }))}>{ACTIONS.map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}</TextField></Grid>
                    <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="datetime-local" label="Cuándo" InputLabelProps={{ shrink: true }} value={form.nextAction.dueAt} onChange={(e) => setForm((c) => ({ ...c, nextAction: { ...c.nextAction, dueAt: e.target.value } }))} /></Grid>
                    <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Objetivo breve" value={form.nextAction.note} onChange={(e) => setForm((c) => ({ ...c, nextAction: { ...c.nextAction, note: e.target.value } }))} placeholder="Confirmar medida / cerrar precio…" /></Grid>
                  </Grid>
                </Paper>
              ) : (
                <TextField fullWidth size="small" label={selectedStage.kind === "won" ? "Qué cerramos" : "Motivo de pérdida"} value={form.closeReason} onChange={(e) => setForm((c) => ({ ...c, closeReason: e.target.value }))} placeholder={selectedStage.kind === "won" ? "Venta, reserva, acuerdo…" : "Precio, sin respuesta, eligió otra opción…"} />
              )}

              <Paper variant="outlined" sx={{ p: 1.25, borderColor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.08)" }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={.7} alignItems={{ sm: "center" }} justifyContent="space-between">
                  <Box><Typography sx={{ fontWeight: 950, color: INK }}>Registrar contacto</Typography><Typography variant="caption" sx={{ color: MUTED }}>Esto alimenta el historial que después puede usar WhatsApp API o la IA.</Typography></Box>
                  <Stack direction="row" spacing={.4}>{ACTIONS.slice(0, 3).map(([key, label, icon]) => <Tooltip key={key} title={label}><IconButton size="small" color={activity.type === key ? "primary" : "default"} onClick={() => setActivity((c) => ({ ...c, type: key }))}>{icon}</IconButton></Tooltip>)}<Tooltip title="Nota"><IconButton size="small" color={activity.type === "note" ? "primary" : "default"} onClick={() => setActivity((c) => ({ ...c, type: "note" }))}><NotesRoundedIcon fontSize="small" /></IconButton></Tooltip></Stack>
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={.8} sx={{ mt: .8 }} alignItems={{ sm: "center" }}>
                  <TextField fullWidth size="small" value={activity.note} onChange={(e) => setActivity((c) => ({ ...c, note: e.target.value }))} placeholder="Ej.: pidió presupuesto, le escribí y quedamos en hablar el viernes…" />
                  <Button variant="contained" onClick={addActivity} disabled={busy} sx={{ whiteSpace: "nowrap", fontWeight: 950 }}>Registrar</Button>
                </Stack>
                <FormControlLabel control={<Checkbox size="small" checked={activity.completeNextAction} onChange={(e) => setActivity((c) => ({ ...c, completeNextAction: e.target.checked }))} />} label={<Typography variant="caption" sx={{ fontWeight: 800 }}>Marcar la próxima acción actual como hecha</Typography>} />
              </Paper>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={4.5}>
            <Paper variant="outlined" sx={{ p: 1.2, height: "100%", borderColor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.08)" }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center"><Typography sx={{ fontWeight: 950, color: INK }}>Historial comercial</Typography><Chip size="small" label={`${activities.length} eventos`} variant="outlined" /></Stack>
              {!activities.length ? <Typography variant="body2" sx={{ mt: 1, color: MUTED }}>Todavía no hay seguimiento. Registrá el primer contacto y esta ficha empieza a contar la historia del prospecto.</Typography> : (
                <Stack spacing={.65} sx={{ mt: 1 }}>
                  {activities.slice(0, 7).map((item) => (
                    <Box key={item.id || `${item.type}-${item.at}`} sx={{ display: "grid", gridTemplateColumns: "28px 1fr", gap: .7, p: .7, borderRadius: 1.5, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.025)" }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: 1.2, display: "grid", placeItems: "center", color: ORANGE, bgcolor: "rgba(243,90,58,.10)" }}>{activityIcon(item)}</Box>
                      <Box sx={{ minWidth: 0 }}><Typography variant="body2" sx={{ fontWeight: 900 }}>{item.title || item.type}</Typography>{item.note ? <Typography variant="caption" sx={{ color: MUTED, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{item.note}</Typography> : null}<Typography variant="caption" sx={{ color: MUTED }}>{dateLabel(item.at)}{item.actor?.name ? ` · ${item.actor.name}` : ""}</Typography></Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 1.6, py: 1.1 }}><Button onClick={onClose} disabled={busy}>Cerrar</Button><Button variant="contained" onClick={save} disabled={busy} sx={{ fontWeight: 950 }}>Guardar seguimiento</Button></DialogActions>
    </Dialog>
  );
}

function newStage(index) {
  return { key: `etapa-${Date.now()}-${index}`, name: "Nueva etapa", kind: "open", color: ORANGE, active: true, order: (index + 1) * 10, defaultNextActionDays: 2, nextActionType: "whatsapp", description: "" };
}

function integrationStatusLabel(snapshot) {
  if (snapshot?.connected || snapshot?.connectionStatus === "connected") return "Conectado";
  if (snapshot?.configured) return "Listo para validar";
  if (snapshot?.connectionStatus === "incomplete") return "Configuración incompleta";
  return "Sin configurar";
}

function integrationStatusSx(snapshot, accent) {
  if (snapshot?.connected || snapshot?.connectionStatus === "connected") return { bgcolor: `${accent}22`, color: INK, borderColor: `${accent}88` };
  if (snapshot?.configured) return { bgcolor: `${accent}12`, color: INK, borderColor: `${accent}44` };
  if (snapshot?.connectionStatus === "incomplete") return { bgcolor: "rgba(239,139,69,.10)", color: INK, borderColor: "rgba(239,139,69,.35)" };
  return { color: MUTED };
}

function defaultWhatsappForm(snapshot = {}) {
  return {
    businessAccountId: snapshot.businessAccountId || "",
    phoneNumberId: snapshot.phoneNumberId || "",
    appId: snapshot.appId || "",
    displayPhoneNumber: snapshot.displayPhoneNumber || "",
    apiVersion: snapshot.apiVersion || "v26.0",
    defaultCountryCode: snapshot.defaultCountryCode || "54",
    accessToken: "",
    appSecret: "",
    verifyToken: "",
  };
}

function defaultAiForm(snapshot = {}) {
  return {
    provider: snapshot.provider || "openai",
    model: snapshot.model || "",
    baseUrl: snapshot.baseUrl || "",
    apiKey: "",
    assistantName: snapshot.assistantName || "Asistente",
    tone: snapshot.tone || "friendly",
    language: snapshot.language || "es-AR",
    businessContext: snapshot.businessContext || "",
    instructions: snapshot.instructions || "",
    handoffMessage: snapshot.handoffMessage || "",
  };
}

function StepRail({ labels, step, onStep }) {
  return (
    <Stack direction="row" spacing={.55} sx={{ mb: 1.25, overflow: "hidden" }}>
      {labels.map((label, index) => {
        const active = step === index;
        const done = index < step;
        return (
          <Button
            key={label}
            size="small"
            onClick={() => onStep(index)}
            sx={{
              minWidth: 0,
              flex: 1,
              px: .6,
              py: .55,
              borderRadius: 1.6,
              textTransform: "none",
              fontWeight: 950,
              fontSize: 11.5,
              whiteSpace: "nowrap",
              color: active ? "#fff" : INK,
              bgcolor: active ? ORANGE : done ? "rgba(243,90,58,.08)" : "rgba(var(--wp-panel-ink-rgb,25,36,47),.035)",
              border: active ? "1px solid transparent" : "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.07)",
              "&:hover": { bgcolor: active ? ORANGE : "rgba(243,90,58,.10)" },
            }}
          >
            {index + 1}. {label}
          </Button>
        );
      })}
    </Stack>
  );
}

function SecretField({ label, value, onChange, configured, helperText }) {
  return (
    <TextField
      fullWidth
      size="small"
      type="password"
      label={label}
      value={value}
      onChange={onChange}
      placeholder={configured ? "Guardado · dejá vacío para conservar" : "Pegá la credencial"}
      helperText={helperText || (configured ? "Ya existe una credencial cifrada. Sólo completá este campo si querés reemplazarla." : "Se guarda cifrada y nunca vuelve al navegador.")}
      FormHelperTextProps={{ sx: { mx: .2, lineHeight: 1.25 } }}
    />
  );
}

function CapabilityCard({ checked, title, detail, onChange, danger = false }) {
  return (
    <Paper
      component="label"
      variant="outlined"
      sx={{
        p: .85,
        cursor: "pointer",
        borderColor: checked ? (danger ? "rgba(239,85,85,.36)" : "rgba(123,97,255,.35)") : "rgba(var(--wp-panel-ink-rgb,25,36,47),.08)",
        bgcolor: checked ? (danger ? "rgba(239,85,85,.035)" : "rgba(123,97,255,.035)") : "transparent",
      }}
    >
      <Stack direction="row" spacing={.65} alignItems="flex-start">
        <Checkbox size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} sx={{ p: .15, mt: .05 }} />
        <Box><Typography variant="body2" sx={{ fontWeight: 950, color: INK }}>{title}</Typography><Typography variant="caption" sx={{ color: MUTED, lineHeight: 1.25 }}>{detail}</Typography></Box>
      </Stack>
    </Paper>
  );
}

function CrmIntegrationSetupDialog({ open, type, projectId, snapshot, aiPolicy, onAiPolicyChange, onClose, onSaved, onDeleted }) {
  const isWhatsapp = type === "whatsapp";
  const labels = isWhatsapp ? ["Cuenta Meta", "App", "Credenciales", "Revisar"] : ["Proveedor", "Identidad", "Negocio", "Reglas", "Revisar"];
  const [step, setStep] = React.useState(0);
  const [currentSnapshot, setCurrentSnapshot] = React.useState(snapshot || null);
  const [form, setForm] = React.useState(() => isWhatsapp ? defaultWhatsappForm(snapshot) : defaultAiForm(snapshot));
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setStep(0);
    setBusy(false);
    setError("");
    setMessage("");
    setCurrentSnapshot(snapshot || null);
    setForm(isWhatsapp ? defaultWhatsappForm(snapshot) : defaultAiForm(snapshot));
    // Se reinicia únicamente al abrir/cambiar de integración. Un save actualiza
    // currentSnapshot sin mandar al usuario otra vez al paso 1.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isWhatsapp]);

  const setValue = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const setPolicy = (key, value) => onAiPolicyChange?.({ ...(aiPolicy || {}), [key]: value });

  const save = async () => {
    setBusy(true); setError(""); setMessage("");
    try {
      const response = await customersApi.saveCrmIntegration(projectId, type, form);
      const saved = response?.integration || null;
      setCurrentSnapshot(saved);
      setMessage(response?.message || "Configuración guardada. Ahora podés validar las credenciales y activar la integración.");
      onSaved?.(type, saved);
      setStep(labels.length - 1);
    } catch (err) {
      setError(err?.message || "No se pudo guardar la configuración.");
    } finally { setBusy(false); }
  };

  const activate = async () => {
    setBusy(true); setError(""); setMessage("");
    try {
      const response = await customersApi.activateCrmIntegration(projectId, type);
      const saved = response?.integration || null;
      setCurrentSnapshot(saved);
      onSaved?.(type, saved);
      setMessage(response?.message || "Integración validada y activada.");
    } catch (err) {
      setError(err?.message || "No se pudo validar o activar la integración.");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("¿Borrar esta configuración y sus credenciales guardadas?")) return;
    setBusy(true); setError(""); setMessage("");
    try {
      await customersApi.deleteCrmIntegration(projectId, type);
      onDeleted?.(type);
      onClose?.();
    } catch (err) {
      setError(err?.message || "No se pudo borrar la configuración.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{ pb: .9, pr: 6 }}>
        <Stack direction="row" spacing={.8} alignItems="center">
          {isWhatsapp ? <WhatsAppIcon sx={{ color: "#25D366" }} /> : <AutoAwesomeRoundedIcon sx={{ color: "#7B61FF" }} />}
          <Box><Typography sx={{ fontWeight: 1000, fontSize: 20 }}>{isWhatsapp ? "Configurar WhatsApp Business API" : "Configurar chatbot IA"}</Typography><Typography variant="caption" sx={{ color: MUTED }}>Guardá las credenciales, validalas contra el proveedor y activá el conector sin salir del CRM.</Typography></Box>
        </Stack>
        <IconButton size="small" onClick={onClose} disabled={busy} sx={{ position: "absolute", right: 12, top: 12 }}><CloseRoundedIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 1.45 }}>
        <StepRail labels={labels} step={step} onStep={setStep} />
        {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
        {message ? <Alert severity="warning" sx={{ mb: 1 }}>{message}</Alert> : null}

        {isWhatsapp && step === 0 ? (
          <Stack spacing={1}>
            <Alert severity="info">En Meta necesitás identificar la cuenta de WhatsApp Business y el número que va a conversar con tus clientes.</Alert>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="WhatsApp Business Account ID (WABA)" value={form.businessAccountId} onChange={(e) => setValue("businessAccountId", e.target.value)} helperText="Identifica la cuenta comercial dentro de Meta." /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Phone Number ID" value={form.phoneNumberId} onChange={(e) => setValue("phoneNumberId", e.target.value)} helperText="Identifica el número que usará la Cloud API." /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Número visible" value={form.displayPhoneNumber} onChange={(e) => setValue("displayPhoneNumber", e.target.value)} placeholder="+54 9 ..." helperText="Sólo referencia para el equipo." /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Código país por defecto" value={form.defaultCountryCode} onChange={(e) => setValue("defaultCountryCode", e.target.value)} placeholder="54" /></Grid>
            </Grid>
          </Stack>
        ) : null}

        {isWhatsapp && step === 1 ? (
          <Stack spacing={1}>
            <Alert severity="info">La App de Meta firma los webhooks y administra los permisos de WhatsApp. WebProfit valida la conexión directamente contra Meta al activar.</Alert>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={7}><TextField fullWidth size="small" label="Meta App ID" value={form.appId} onChange={(e) => setValue("appId", e.target.value)} /></Grid>
              <Grid item xs={12} sm={5}><TextField fullWidth size="small" label="Versión Graph API" value={form.apiVersion} onChange={(e) => setValue("apiVersion", e.target.value)} placeholder="v26.0" helperText="Podés cambiarla si tu App de Meta requiere otra versión soportada." /></Grid>
            </Grid>
            <Paper variant="outlined" sx={{ p: 1, borderColor: "rgba(37,211,102,.20)", bgcolor: "rgba(37,211,102,.025)" }}><Typography variant="body2" sx={{ fontWeight: 950 }}>Webhook público</Typography><Typography variant="caption" sx={{ color: MUTED }}>En Meta configurá como callback el dominio público de este backend + <strong>{currentSnapshot?.webhook?.path || `/api/webhooks/whatsapp/${projectId}`}</strong>. El Verify Token de abajo se usa para completar la verificación.</Typography></Paper>
          </Stack>
        ) : null}

        {isWhatsapp && step === 2 ? (
          <Stack spacing={1}>
            <Alert severity="warning">Estas credenciales se guardan cifradas. El frontend sólo vuelve a saber si existen; nunca recibe el valor guardado.</Alert>
            <SecretField label="Access Token" value={form.accessToken} onChange={(e) => setValue("accessToken", e.target.value)} configured={currentSnapshot?.credentials?.hasAccessToken} helperText={currentSnapshot?.credentials?.hasAccessToken ? "Ya existe un token cifrado. Dejalo vacío para conservarlo." : "Necesario para que WebProfit pueda enviar mensajes y validar el número contra Meta."} />
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}><SecretField label="App Secret" value={form.appSecret} onChange={(e) => setValue("appSecret", e.target.value)} configured={currentSnapshot?.credentials?.hasAppSecret} helperText="Se usará para validar que los webhooks vienen de Meta." /></Grid>
              <Grid item xs={12} sm={6}><SecretField label="Verify Token" value={form.verifyToken} onChange={(e) => setValue("verifyToken", e.target.value)} configured={currentSnapshot?.credentials?.hasVerifyToken} helperText="Token privado que usarán Meta y WebProfit durante la verificación del webhook." /></Grid>
            </Grid>
          </Stack>
        ) : null}

        {isWhatsapp && step === 3 ? (
          <Stack spacing={1}>
            <Alert severity={currentSnapshot?.connected ? "success" : currentSnapshot?.configured ? "warning" : "info"}>{currentSnapshot?.connected ? "WhatsApp está conectado. Los mensajes entrantes ya pueden poblar la bandeja del CRM y los salientes usan la Cloud API." : currentSnapshot?.configured ? "Las credenciales están guardadas. Para conectar, completá también App Secret y Verify Token y ejecutá la validación." : "Guardá WABA ID, Phone Number ID, Access Token, App Secret y Verify Token para validar el conector."}</Alert>
            <Paper variant="outlined" sx={{ p: 1.1 }}>
              <Stack spacing={.55}>
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2" sx={{ fontWeight: 900 }}>WABA</Typography><Typography variant="body2">{form.businessAccountId || "Pendiente"}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2" sx={{ fontWeight: 900 }}>Número API</Typography><Typography variant="body2">{form.phoneNumberId || "Pendiente"}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2" sx={{ fontWeight: 900 }}>Access Token</Typography><Typography variant="body2">{form.accessToken || currentSnapshot?.credentials?.hasAccessToken ? "Configurado" : "Pendiente"}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2" sx={{ fontWeight: 900 }}>Estado real</Typography><Chip size="small" label={currentSnapshot?.connected ? "Conectado" : currentSnapshot?.configured ? "Listo para validar" : "Incompleto"} variant="outlined" /></Stack>
              </Stack>
            </Paper>
            <Button variant="outlined" onClick={activate} disabled={busy || !currentSnapshot?.configured} sx={{ alignSelf: "flex-start", fontWeight: 950 }}>{currentSnapshot?.connected ? "Revalidar conexión" : "Validar y activar"}</Button>
          </Stack>
        ) : null}

        {!isWhatsapp && step === 0 ? (
          <Stack spacing={1}>
            <Alert severity="info">Elegí proveedor y modelo. Al activar, WebProfit valida la credencial contra el proveedor y habilita el copiloto de respuestas.</Alert>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4}><TextField select fullWidth size="small" label="Proveedor" value={form.provider} onChange={(e) => setValue("provider", e.target.value)}><MenuItem value="openai">OpenAI</MenuItem><MenuItem value="anthropic">Anthropic</MenuItem><MenuItem value="google">Google</MenuItem><MenuItem value="custom">Compatible / propio</MenuItem></TextField></Grid>
              <Grid item xs={12} sm={8}><TextField fullWidth size="small" label="Modelo" value={form.model} onChange={(e) => setValue("model", e.target.value)} placeholder="Ej.: modelo que decidas usar" helperText="No lo fijamos en código: podés cambiarlo sin desplegar WebProfit." /></Grid>
              {form.provider === "custom" ? <Grid item xs={12}><TextField fullWidth size="small" label="Base URL del proveedor" value={form.baseUrl} onChange={(e) => setValue("baseUrl", e.target.value)} placeholder="https://..." /></Grid> : null}
              <Grid item xs={12}><SecretField label="API key" value={form.apiKey} onChange={(e) => setValue("apiKey", e.target.value)} configured={currentSnapshot?.credentials?.hasApiKey} /></Grid>
            </Grid>
          </Stack>
        ) : null}

        {!isWhatsapp && step === 1 ? (
          <Stack spacing={1}>
            <Typography sx={{ fontWeight: 950 }}>¿Quién habla cuando responde la IA?</Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={5}><TextField fullWidth size="small" label="Nombre del asistente" value={form.assistantName} onChange={(e) => setValue("assistantName", e.target.value)} placeholder="Ej.: Sofi de El Roble" /></Grid>
              <Grid item xs={12} sm={4}><TextField select fullWidth size="small" label="Tono" value={form.tone} onChange={(e) => setValue("tone", e.target.value)}><MenuItem value="friendly">Cercano</MenuItem><MenuItem value="professional">Profesional</MenuItem><MenuItem value="direct">Directo</MenuItem><MenuItem value="sales">Comercial</MenuItem></TextField></Grid>
              <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Idioma" value={form.language} onChange={(e) => setValue("language", e.target.value)} /></Grid>
              <Grid item xs={12}><TextField fullWidth size="small" label="Mensaje al derivar a una persona" value={form.handoffMessage} onChange={(e) => setValue("handoffMessage", e.target.value)} placeholder="Te paso con una persona del equipo para seguir desde acá." /></Grid>
            </Grid>
          </Stack>
        ) : null}

        {!isWhatsapp && step === 2 ? (
          <Stack spacing={1}>
            <Alert severity="info">Esto es más importante que un prompt gigante: contale a la IA qué vende el negocio, a quién, cómo cotiza y qué nunca debería inventar.</Alert>
            <TextField fullWidth multiline minRows={3} maxRows={5} label="Contexto del negocio" value={form.businessContext} onChange={(e) => setValue("businessContext", e.target.value)} placeholder="Vendemos..., atendemos..., los precios..., las reservas..., las zonas..." />
            <TextField fullWidth multiline minRows={3} maxRows={5} label="Reglas e instrucciones" value={form.instructions} onChange={(e) => setValue("instructions", e.target.value)} placeholder="No confirmar descuentos sin aprobación. No inventar stock. Si falta información, preguntar..." />
          </Stack>
        ) : null}

        {!isWhatsapp && step === 3 ? (
          <Stack spacing={.75}>
            <Alert severity="info">Estas reglas usan la configuración de seguimiento que ya existe en el CRM. No crean otra política paralela.</Alert>
            <TextField select fullWidth size="small" label="Modo de IA" value={aiPolicy?.mode || "assist"} onChange={(e) => setPolicy("mode", e.target.value)}>
              <MenuItem value="off">Apagada</MenuItem>
              <MenuItem value="assist">Asistente con aprobación</MenuItem>
              <MenuItem value="automation">Automatización controlada</MenuItem>
            </TextField>
            <Grid container spacing={.75}>
              <Grid item xs={12} sm={6}><CapabilityCard checked={aiPolicy?.draftMessages !== false} onChange={(v) => setPolicy("draftMessages", v)} title="Redactar respuestas" detail="Propone el mensaje usando la ficha y el historial del prospecto." /></Grid>
              <Grid item xs={12} sm={6}><CapabilityCard checked={aiPolicy?.suggestNextAction !== false} onChange={(v) => setPolicy("suggestNextAction", v)} title="Sugerir próxima acción" detail="Propone qué hacer y cuándo para que nadie quede olvidado." /></Grid>
              <Grid item xs={12} sm={6}><CapabilityCard checked={aiPolicy?.suggestStage !== false} onChange={(v) => setPolicy("suggestStage", v)} title="Sugerir etapa" detail="Puede recomendar pasar a Contactado, Propuesta, Ganado, etc." /></Grid>
              <Grid item xs={12} sm={6}><CapabilityCard danger checked={aiPolicy?.autoMoveStage === true} onChange={(v) => setPolicy("autoMoveStage", v)} title="Mover etapa automáticamente" detail="Política preparada: el motor conversacional todavía no cambia etapas por sí solo; sí puede sugerirlas." /></Grid>
              <Grid item xs={12} sm={6}><CapabilityCard danger checked={aiPolicy?.autoCloseWon === true} onChange={(v) => setPolicy("autoCloseWon", v)} title="Cerrar como ganado" detail="Política preparada: el motor no cierra oportunidades automáticamente en esta versión." /></Grid>
              <Grid item xs={12} sm={6}><CapabilityCard danger checked={aiPolicy?.autoCloseLost === true} onChange={(v) => setPolicy("autoCloseLost", v)} title="Cerrar como perdido" detail="Política preparada: el motor no cierra oportunidades automáticamente en esta versión." /></Grid>
            </Grid>
            <CapabilityCard checked={aiPolicy?.humanApprovalRequired !== false} onChange={(v) => setPolicy("humanApprovalRequired", v)} title="Pedir aprobación humana" detail="Recomendado para mensajes, cambios de etapa y cualquier cierre comercial." />
            <Typography variant="caption" sx={{ color: MUTED }}>Estas reglas se guardan con “Guardar seguimiento” en la ventana principal.</Typography>
          </Stack>
        ) : null}

        {!isWhatsapp && step === 4 ? (
          <Stack spacing={1}>
            <Alert severity={currentSnapshot?.connected ? "success" : currentSnapshot?.configured ? "warning" : "info"}>{currentSnapshot?.connected ? "La IA está conectada. En modo Asistente redacta borradores; en Automatización puede responder WhatsApp si desactivás la aprobación humana." : currentSnapshot?.configured ? "Proveedor, modelo y credencial están guardados. Validalos para habilitar el motor de IA." : "Para habilitar la IA necesitás proveedor, modelo y API key."}</Alert>
            <Paper variant="outlined" sx={{ p: 1.1 }}>
              <Stack spacing={.55}>
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2" sx={{ fontWeight: 900 }}>Proveedor</Typography><Typography variant="body2">{form.provider}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2" sx={{ fontWeight: 900 }}>Modelo</Typography><Typography variant="body2">{form.model || "Pendiente"}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2" sx={{ fontWeight: 900 }}>API key</Typography><Typography variant="body2">{form.apiKey || currentSnapshot?.credentials?.hasApiKey ? "Configurada" : "Pendiente"}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2" sx={{ fontWeight: 900 }}>Modo de IA</Typography><Typography variant="body2">{aiPolicy?.mode === "automation" ? "Automatización controlada" : aiPolicy?.mode === "off" ? "Apagada" : "Asistente"}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2" sx={{ fontWeight: 900 }}>Estado real</Typography><Chip size="small" label={currentSnapshot?.connected ? "Conectado" : currentSnapshot?.configured ? "Listo para validar" : "Incompleto"} variant="outlined" /></Stack>
              </Stack>
            </Paper>
            <Button variant="outlined" onClick={activate} disabled={busy || !currentSnapshot?.configured} sx={{ alignSelf: "flex-start", fontWeight: 950 }}>{currentSnapshot?.connected ? "Revalidar conexión" : "Validar y activar"}</Button>
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 1.45, py: 1.05, justifyContent: "space-between" }}>
        <Button color="error" onClick={remove} disabled={busy || !currentSnapshot?.updatedAt} startIcon={<DeleteOutlineRoundedIcon />}>Borrar configuración</Button>
        <Stack direction="row" spacing={.65}>
          {step > 0 ? <Button onClick={() => setStep((current) => Math.max(0, current - 1))} disabled={busy}>Anterior</Button> : null}
          {step < labels.length - 1 ? <Button variant="contained" onClick={() => setStep((current) => Math.min(labels.length - 1, current + 1))} disabled={busy} sx={{ fontWeight: 950 }}>Siguiente</Button> : <Button variant="contained" onClick={save} disabled={busy} sx={{ fontWeight: 950 }}>Guardar configuración</Button>}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

export function CrmWorkflowConfigDialog({ open, config, busy = false, onClose, onSave }) {
  const { projectId } = useParams();
  const [form, setForm] = React.useState(config);
  const [error, setError] = React.useState("");
  const [integrations, setIntegrations] = React.useState(null);
  const [integrationsBusy, setIntegrationsBusy] = React.useState(false);
  const [setupType, setSetupType] = React.useState("");

  const loadIntegrations = React.useCallback(async () => {
    if (!projectId) return;
    setIntegrationsBusy(true);
    try {
      const response = await customersApi.crmIntegrations(projectId);
      setIntegrations(response?.integrations || null);
    } catch (err) {
      setError(err?.message || "No se pudo leer la configuración de automatizaciones.");
    } finally { setIntegrationsBusy(false); }
  }, [projectId]);

  React.useEffect(() => {
    if (open) {
      setForm(config);
      setError("");
      loadIntegrations();
    }
  }, [config, open, loadIntegrations]);
  if (!form) return null;

  const updateStage = (index, patch) => setForm((current) => ({ ...current, stages: current.stages.map((stage, idx) => idx === index ? { ...stage, ...patch } : stage) }));
  const move = (index, delta) => setForm((current) => { const stages = [...current.stages]; const target = index + delta; if (target < 0 || target >= stages.length) return current; [stages[index], stages[target]] = [stages[target], stages[index]]; return { ...current, stages: stages.map((stage, idx) => ({ ...stage, order: (idx + 1) * 10 })) }; });
  const remove = (index) => setForm((current) => ({ ...current, stages: current.stages.filter((_stage, idx) => idx !== index) }));

  const save = async () => {
    if (!form.stages?.some((stage) => stage.kind === "open" && stage.active !== false)) { setError("Necesitás al menos una etapa abierta activa."); return; }
    setError("");
    try { await onSave?.(form); } catch (err) { setError(err?.message || "No se pudo guardar la configuración."); }
  };

  const integrationSaved = (key, snapshot) => {
    setIntegrations((current) => ({ ...(current || {}), [key]: snapshot || current?.[key] }));
  };
  const integrationDeleted = (key) => {
    setIntegrations((current) => ({ ...(current || {}), [key]: null }));
  };

  return (
    <>
      <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pr: 6 }}><Typography sx={{ fontWeight: 1000, fontSize: 20 }}>Configurar seguimiento comercial</Typography><Typography variant="body2" sx={{ color: MUTED }}>Definí el pipeline y conectá WhatsApp Business + IA sobre la misma ficha comercial, sin duplicar datos.</Typography><IconButton size="small" onClick={onClose} sx={{ position: "absolute", right: 12, top: 12 }}><CloseRoundedIcon /></IconButton></DialogTitle>
        <DialogContent dividers sx={{ p: 1.6 }}>
          {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
          <Grid container spacing={1.2}>
            <Grid item xs={12} md={8.5}>
              <Paper variant="outlined" sx={{ p: 1.25 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} justifyContent="space-between"><Box><Typography sx={{ fontWeight: 950 }}>Etapas del pipeline</Typography><Typography variant="caption" sx={{ color: MUTED }}>ABM: agregá, editá, ordená o desactivá etapas. No necesitás tocar código.</Typography></Box><Button size="small" startIcon={<AddRoundedIcon />} onClick={() => setForm((c) => ({ ...c, stages: [...c.stages, newStage(c.stages.length)] }))}>Agregar etapa</Button></Stack>
                <Stack spacing={.65} sx={{ mt: 1 }}>
                  {form.stages.map((stage, index) => (
                    <Box key={`${stage.key}-${index}`} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "28px minmax(130px,1.1fr) 100px 74px 120px 74px 86px" }, gap: .55, alignItems: "center", p: .65, borderRadius: 1.5, bgcolor: stage.active === false ? "rgba(var(--wp-panel-ink-rgb,25,36,47),.035)" : "transparent", border: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.07)" }}>
                      <Box sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: stage.color, mx: "auto" }} />
                      <TextField size="small" label="Nombre" value={stage.name} onChange={(e) => updateStage(index, { name: e.target.value })} />
                      <TextField select size="small" label="Tipo" value={stage.kind} onChange={(e) => updateStage(index, { kind: e.target.value })}><MenuItem value="open">Abierta</MenuItem><MenuItem value="won">Ganada</MenuItem><MenuItem value="lost">Perdida</MenuItem></TextField>
                      <TextField size="small" label="Días" type="number" value={stage.defaultNextActionDays} onChange={(e) => updateStage(index, { defaultNextActionDays: e.target.value })} disabled={stage.kind !== "open"} />
                      <TextField select size="small" label="Acción" value={stage.nextActionType || ""} onChange={(e) => updateStage(index, { nextActionType: e.target.value })} disabled={stage.kind !== "open"}><MenuItem value="">Ninguna</MenuItem>{ACTIONS.map(([key,label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}</TextField>
                      <TextField size="small" label="Color" type="color" value={stage.color || ORANGE} onChange={(e) => updateStage(index, { color: e.target.value })} InputLabelProps={{ shrink: true }} />
                      <Stack direction="row" spacing={.15} justifyContent="flex-end"><Tooltip title="Subir"><span><IconButton size="small" disabled={index === 0} onClick={() => move(index, -1)}><ArrowUpwardRoundedIcon fontSize="small" /></IconButton></span></Tooltip><Tooltip title="Bajar"><span><IconButton size="small" disabled={index === form.stages.length - 1} onClick={() => move(index, 1)}><ArrowDownwardRoundedIcon fontSize="small" /></IconButton></span></Tooltip><Tooltip title="Quitar"><span><IconButton size="small" color="error" disabled={form.stages.length <= 3} onClick={() => remove(index)}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton></span></Tooltip></Stack>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3.5}>
              <Stack spacing={1}>
                <Paper variant="outlined" sx={{ p: 1.2, borderColor: "rgba(37,211,102,.24)", bgcolor: "rgba(37,211,102,.02)" }}>
                  <Stack direction="row" spacing={.7} alignItems="center"><WhatsAppIcon sx={{ color: "#25D366" }} /><Box sx={{ minWidth: 0 }}><Typography sx={{ fontWeight: 950 }}>WhatsApp Business</Typography><Typography variant="caption" sx={{ color: MUTED }}>Meta Cloud API · configuración real</Typography></Box></Stack>
                  <Typography variant="body2" sx={{ mt: .7, color: MUTED }}>Cargá WABA, número, App y credenciales. WebProfit valida Meta Cloud API, recibe webhooks y centraliza las conversaciones en el CRM.</Typography>
                  <Stack direction="row" spacing={.6} alignItems="center" justifyContent="space-between" sx={{ mt: .9 }}><Chip size="small" label={integrationsBusy ? "Leyendo…" : integrationStatusLabel(integrations?.whatsapp)} variant="outlined" sx={{ fontWeight: 850, ...integrationStatusSx(integrations?.whatsapp, "#25D366") }} /><Button size="small" onClick={() => setSetupType("whatsapp")} sx={{ fontWeight: 950 }}>Configurar</Button></Stack>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.2, borderColor: "rgba(123,97,255,.24)", bgcolor: "rgba(123,97,255,.02)" }}>
                  <Stack direction="row" spacing={.7} alignItems="center"><AutoAwesomeRoundedIcon sx={{ color: "#7B61FF" }} /><Box sx={{ minWidth: 0 }}><Typography sx={{ fontWeight: 950 }}>Chatbot IA</Typography><Typography variant="caption" sx={{ color: MUTED }}>Proveedor + modelo + reglas</Typography></Box></Stack>
                  <Typography variant="body2" sx={{ mt: .7, color: MUTED }}>Definí proveedor, modelo, personalidad y reglas. Puede trabajar como copiloto o responder automáticamente con handoff humano.</Typography>
                  <Stack direction="row" spacing={.6} alignItems="center" justifyContent="space-between" sx={{ mt: .9 }}><Chip size="small" label={integrationsBusy ? "Leyendo…" : integrationStatusLabel(integrations?.ai)} variant="outlined" sx={{ fontWeight: 850, ...integrationStatusSx(integrations?.ai, "#7B61FF") }} /><Button size="small" onClick={() => setSetupType("ai")} sx={{ fontWeight: 950 }}>Configurar</Button></Stack>
                </Paper>
                <TextField fullWidth size="small" label="Días sin contacto = atención" type="number" value={form.staleAfterDays || 7} onChange={(e) => setForm((c) => ({ ...c, staleAfterDays: e.target.value }))} helperText="Sirve para detectar prospectos olvidados." />
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 1.6, py: 1.1 }}><Button onClick={onClose} disabled={busy}>Cancelar</Button><Button variant="contained" onClick={save} disabled={busy} sx={{ fontWeight: 950 }}>Guardar seguimiento</Button></DialogActions>
      </Dialog>

      <CrmIntegrationSetupDialog
        open={Boolean(setupType)}
        type={setupType || "whatsapp"}
        projectId={projectId}
        snapshot={setupType ? integrations?.[setupType] : null}
        aiPolicy={form.automation?.ai || {}}
        onAiPolicyChange={(ai) => setForm((current) => ({ ...current, automation: { ...current.automation, ai } }))}
        onClose={() => setSetupType("")}
        onSaved={integrationSaved}
        onDeleted={integrationDeleted}
      />
    </>
  );
}
